import React from 'react';
import type { Api } from '../api';
import { ReactError } from '../error/errors';
import type { CleanupFunction } from '../brands';
import { noop } from '@pdcfrontendui/utils';

export type ErrorBoundaryProps = {
  children: React.ReactNode;
  /**
   * The type of component that children is.
   */
  type: ReactError['type'];
  fallback?: React.ReactNode;
  logRetry?: number;
};

export type ErrorBoundaryState = {
  hasError: boolean;
};

export const errorBoundaryListeners = new Set<() => void>();
export function addErrorBoundaryListener(listener: () => void) {
  errorBoundaryListeners.add(listener);
  return (() => {
    errorBoundaryListeners.delete(listener);
  }) as CleanupFunction;
}

export function makeErrorBoundary(api: Pick<Api, 'log'>, logRetry?: number) {
  /**
   * An error boundary that catches errors in its children and logs them to the API.
   * It also provides a optional fallback UI in case of an error.
   */
  class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
  > {
    constructor(props: ErrorBoundaryProps) {
      super(props);
      this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
      return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
      const componentStack = errorInfo.componentStack
        ?.split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 4)
        .join(', ');
      let message = 'error.message: ' + error.message;
      if (componentStack) {
        message += '\n\nerrorInfo.componentStack: ' + componentStack;
      }

      errorBoundaryListeners.forEach((listener) => {
        listener();
      });
      api
        .log(
          new ReactError({
            message,
            type: this.props.type,
          }),
          { allowErrors: false, retry: logRetry ?? this.props.logRetry ?? 0 }
        )
        .catch(noop);
    }

    render(): React.ReactNode {
      return this.state.hasError
        ? (this.props.fallback ?? null)
        : this.props.children;
    }
  }
  return ErrorBoundary;
}
